const TEAM_COLOR_CSV_PATH = '/team_logo_colors.csv'
const DEFAULT_AWAY_COLOR = '#ef4444'
const DEFAULT_HOME_COLOR = '#3b82f6'
const DEFAULT_OVER_COLOR = DEFAULT_AWAY_COLOR
const DEFAULT_UNDER_COLOR = DEFAULT_HOME_COLOR
const MIN_MATCHUP_COLOR_DISTANCE = 92

let teamColorLookupPromise = null

export const defaultDraftKingsColors = {
  away: DEFAULT_AWAY_COLOR,
  home: DEFAULT_HOME_COLOR,
  over: DEFAULT_OVER_COLOR,
  under: DEFAULT_UNDER_COLOR
}

export async function loadTeamColorLookup() {
  if (!teamColorLookupPromise) {
    teamColorLookupPromise = fetch(TEAM_COLOR_CSV_PATH)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load team colors (${response.status})`)
        }
        return response.text()
      })
      .then(createTeamColorLookup)
      .catch((error) => {
        console.warn(error)
        return createEmptyTeamColorLookup()
      })
  }

  return teamColorLookupPromise
}

export function resolveMatchupColors(lookup, { league, awayTeam, homeTeam }) {
  const away = lookup?.getTeamColors?.(awayTeam, league)
  const home = lookup?.getTeamColors?.(homeTeam, league)

  if (!away || !home) {
    return {
      matched: false,
      away: DEFAULT_AWAY_COLOR,
      home: DEFAULT_HOME_COLOR,
      over: DEFAULT_OVER_COLOR,
      under: DEFAULT_UNDER_COLOR
    }
  }

  const matchupColors = resolveDistinctMatchupColors(away, home)

  return {
    matched: true,
    away: matchupColors.away,
    home: matchupColors.home,
    over: DEFAULT_OVER_COLOR,
    under: DEFAULT_UNDER_COLOR
  }
}

function createTeamColorLookup(csvText) {
  const rows = parseCsv(csvText)
  const [header = [], ...records] = rows
  const indexes = header.reduce((acc, column, index) => {
    acc[normalizeHeader(column)] = index
    return acc
  }, {})

  const byLeagueTeam = new Map()
  const byTeam = new Map()

  records.forEach((record) => {
    const teamName = cleanCell(record[indexes.teamname])
    const league = normalizeLeagueKey(record[indexes.league])
    const primary = normalizeHex(record[indexes.primary])
    const secondary = normalizeHex(record[indexes.secondary])

    if (!teamName || !league || !primary) return

    const entry = { teamName, league, primary, secondary }
    const teamKey = normalizeTeamKey(teamName)
    if (!teamKey) return

    byLeagueTeam.set(`${league}__${teamKey}`, entry)

    const existing = byTeam.get(teamKey)
    if (!existing) {
      byTeam.set(teamKey, entry)
    } else if (Array.isArray(existing)) {
      existing.push(entry)
    } else {
      byTeam.set(teamKey, [existing, entry])
    }
  })

  return {
    getTeamColors(teamName, league) {
      const teamKey = normalizeTeamKey(teamName)
      if (!teamKey) return null

      const leagueKey = normalizeLeagueKey(league)
      if (leagueKey) {
        const leagueMatch = byLeagueTeam.get(`${leagueKey}__${teamKey}`)
        if (leagueMatch) return leagueMatch
      }

      const teamMatch = byTeam.get(teamKey)
      return Array.isArray(teamMatch) ? null : teamMatch || null
    }
  }
}

function createEmptyTeamColorLookup() {
  return {
    getTeamColors() {
      return null
    }
  }
}

function parseCsv(csvText = '') {
  const rows = []
  let row = []
  let cell = ''
  let inQuotes = false

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index]
    const next = csvText[index + 1]

    if (char === '"' && next === '"') {
      cell += '"'
      index += 1
      continue
    }

    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }

    if (char === ',' && !inQuotes) {
      row.push(cell)
      cell = ''
      continue
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1
      row.push(cell)
      if (row.some((value) => value.trim() !== '')) rows.push(row)
      row = []
      cell = ''
      continue
    }

    cell += char
  }

  row.push(cell)
  if (row.some((value) => value.trim() !== '')) rows.push(row)

  return rows
}

function normalizeHeader(value) {
  return cleanCell(value).toLowerCase().replace(/[^a-z0-9]/g, '')
}

function cleanCell(value) {
  return String(value || '').trim()
}

function normalizeLeagueKey(value) {
  const normalized = cleanCell(value).toLowerCase().replace(/[^a-z0-9]/g, '')
  const leagueMap = {
    americanfootballnfl: 'nfl',
    baseballmlb: 'mlb',
    basketballnba: 'nba',
    basketballncaab: 'ncaab',
    basketballncaam: 'ncaab',
    basketballwnba: 'wnba',
    icehockeynhl: 'nhl'
  }

  return leagueMap[normalized] || normalized
}

function normalizeTeamKey(value) {
  return cleanCell(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\bsaint\b/g, 'st')
    .replace(/\bstate\b/g, 'st')
    .replace(/\buniversity\b/g, 'univ')
    .replace(/\bcollege\b/g, 'coll')
    .replace(/\bmount\b/g, 'mt')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

function normalizeHex(value) {
  const hex = cleanCell(value).toUpperCase()
  return /^#[0-9A-F]{6}$/.test(hex) ? hex : ''
}

function readableChartColor(primary, secondary, fallback) {
  if (isReadableOnDark(primary)) return primary
  if (secondary && isReadableOnDark(secondary)) return secondary
  return fallback
}

function resolveDistinctMatchupColors(away, home) {
  const awayCandidates = createTeamColorCandidates(away, DEFAULT_AWAY_COLOR)
  const homeCandidates = createTeamColorCandidates(home, DEFAULT_HOME_COLOR)
  let bestPair = null

  awayCandidates.forEach((awayCandidate) => {
    homeCandidates.forEach((homeCandidate) => {
      const distance = colorDistance(awayCandidate.color, homeCandidate.color)
      const score =
        distance +
        awayCandidate.score +
        homeCandidate.score +
        sameHuePenalty(awayCandidate.color, homeCandidate.color) -
        (distance < MIN_MATCHUP_COLOR_DISTANCE ? 200 : 0)

      if (!bestPair || score > bestPair.score) {
        bestPair = { away: awayCandidate.color, home: homeCandidate.color, score, distance }
      }
    })
  })

  if (bestPair && bestPair.distance >= MIN_MATCHUP_COLOR_DISTANCE) {
    return bestPair
  }

  return {
    away: readableChartColor(away.primary, away.secondary, DEFAULT_AWAY_COLOR),
    home: readableChartColor(home.primary, home.secondary, DEFAULT_HOME_COLOR)
  }
}

function createTeamColorCandidates(teamColors, fallback) {
  return uniqueColors([
    createColorCandidate(teamColors.primary, 90, false),
    createColorCandidate(teamColors.secondary, 82, false),
    createColorCandidate(makeReadableOnDark(teamColors.primary), 74, true),
    createColorCandidate(makeReadableOnDark(teamColors.secondary), 66, true),
    createColorCandidate(fallback, -80)
  ]).filter(Boolean)
}

function createColorCandidate(color, score, isAdjusted = false) {
  if (!color || !isReadableOnDark(color)) return null
  return { color, score, isAdjusted }
}

function uniqueColors(candidates) {
  const seen = new Set()
  return candidates.filter((candidate) => {
    if (!candidate || seen.has(candidate.color)) return false
    seen.add(candidate.color)
    return true
  })
}

function makeReadableOnDark(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return ''

  let adjusted = { ...rgb }
  let attempts = 0
  while (!isReadableOnDark(rgbToHex(adjusted)) && attempts < 10) {
    adjusted = {
      r: Math.round(adjusted.r + (255 - adjusted.r) * 0.16),
      g: Math.round(adjusted.g + (255 - adjusted.g) * 0.16),
      b: Math.round(adjusted.b + (255 - adjusted.b) * 0.16)
    }
    attempts += 1
  }

  return isReadableOnDark(rgbToHex(adjusted)) ? rgbToHex(adjusted) : ''
}

function colorDistance(firstHex, secondHex) {
  const first = hexToRgb(firstHex)
  const second = hexToRgb(secondHex)
  if (!first || !second) return 0
  return Math.sqrt(
    (first.r - second.r) ** 2 +
    (first.g - second.g) ** 2 +
    (first.b - second.b) ** 2
  )
}

function sameHuePenalty(firstHex, secondHex) {
  const first = hexToHsl(firstHex)
  const second = hexToHsl(secondHex)
  if (!first || !second) return 0
  const hueDistance = Math.min(Math.abs(first.h - second.h), 360 - Math.abs(first.h - second.h))
  if (hueDistance >= 24) return 0
  return -120
}

function isReadableOnDark(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return false
  const luminance = relativeLuminance(rgb)
  const darkLuminance = relativeLuminance({ r: 8, g: 13, b: 27 })
  return (Math.max(luminance, darkLuminance) + 0.05) / (Math.min(luminance, darkLuminance) + 0.05) >= 2.2
}

function hexToRgb(hex) {
  const match = /^#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})$/i.exec(hex)
  if (!match) return null
  return {
    r: Number.parseInt(match[1], 16),
    g: Number.parseInt(match[2], 16),
    b: Number.parseInt(match[3], 16)
  }
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`
}

function hexToHsl(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  const red = rgb.r / 255
  const green = rgb.g / 255
  const blue = rgb.b / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const delta = max - min
  let hue = 0

  if (delta !== 0) {
    if (max === red) hue = 60 * (((green - blue) / delta) % 6)
    else if (max === green) hue = 60 * ((blue - red) / delta + 2)
    else hue = 60 * ((red - green) / delta + 4)
  }

  if (hue < 0) hue += 360

  return {
    h: hue,
    s: max === 0 ? 0 : delta / max,
    l: (max + min) / 2
  }
}

function relativeLuminance({ r, g, b }) {
  const [red, green, blue] = [r, g, b].map((channel) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}
